import { useEffect, useMemo, useRef, type DragEvent, type RefObject } from "react";
import { createDragScroller } from "./dragScroller";

export function useDragScroll(target: RefObject<HTMLElement>, enabled: boolean) {
  const activeZone = useRef<HTMLElement | null>(null);
  const scroller = useMemo(() => createDragScroller({
    getTarget: () => activeZone.current?.isConnected ? target.current : null,
    requestFrame: (callback) => window.requestAnimationFrame(callback),
    cancelFrame: (id) => window.cancelAnimationFrame(id),
    now: () => performance.now(),
  }), [target]);

  useEffect(() => {
    const stop = () => { activeZone.current = null; scroller.stop(); };
    if (!enabled) { stop(); return; }
    const dragover = (event: globalThis.DragEvent) => {
      if (activeZone.current && !activeZone.current.contains(event.target as Node)) stop();
    };
    const visibility = () => { if (document.hidden) stop(); };
    document.addEventListener("dragover", dragover, true);
    document.addEventListener("drop", stop, true);
    document.addEventListener("dragend", stop, true);
    document.addEventListener("dragstart", stop, true);
    document.addEventListener("visibilitychange", visibility);
    window.addEventListener("blur", stop);
    return () => {
      stop();
      document.removeEventListener("dragover", dragover, true);
      document.removeEventListener("drop", stop, true);
      document.removeEventListener("dragend", stop, true);
      document.removeEventListener("dragstart", stop, true);
      document.removeEventListener("visibilitychange", visibility);
      window.removeEventListener("blur", stop);
    };
  }, [enabled, scroller]);

  const zone = (direction: -1 | 1) => {
    const enter = (event: DragEvent<HTMLElement>) => {
      event.preventDefault();
      if (!enabled) return;
      activeZone.current = event.currentTarget;
      scroller.start(direction);
    };
    return {
      onDragEnter: enter,
      onDragOver: enter,
      onDragLeave: (event: DragEvent<HTMLElement>) => {
        if (activeZone.current !== event.currentTarget) return;
        if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) return;
        const bounds = event.currentTarget.getBoundingClientRect();
        if (event.clientX >= bounds.left && event.clientX < bounds.right && event.clientY >= bounds.top && event.clientY < bounds.bottom) return;
        activeZone.current = null;
        scroller.stop();
      },
      onDrop: () => { activeZone.current = null; scroller.stop(); },
    };
  };
  return { topZone: zone(-1), bottomZone: zone(1) };
}
